import { TaskStatus } from "griptonite-api-client/types/tasks";
import { useCallback } from "react";

import { Task } from "@/core/types";
import { useOptimisticTaskUpdateMutation } from "@/hooks/tasks";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { useModal } from "../modals/useModal";

/**
 * Changes a task's status, confirming first where leaving Done has a visible
 * effect on the gym: a setting task unpublishes its route, a stripping task
 * restores it. Used by both the drag-and-drop board and the status dropdown in
 * the task sidebar, so the confirmation can't be sidestepped by picking one
 * over the other.
 */
export const useTaskStatusChange = () => {
  const venueUuid = useCurrentVenueUuid();
  const { display: displayModal } = useModal();
  const mutation = useOptimisticTaskUpdateMutation(venueUuid);
  const mutate = mutation.mutate;

  return useCallback(
    (task: Task, status: TaskStatus) => {
      if (task.status === status) {
        return;
      }

      if (task.status === "DONE") {
        if (task.type === "SET") {
          if (task.route?.endTime) {
            // Unpublishing a stripped route would archive a route that was
            // never published. Caught here as well as in the mutation so we
            // don't ask for confirmation of something that can't happen.
            displayModal("TASK_VALIDATION_FAILED", {
              title: "Cannot uncomplete task",
              message:
                "This route has been stripped. Restore it first by moving its stripping task out of Done.",
            });
            return;
          }
          displayModal("CONFIRM_UNCOMPLETE_SETTING_TASK", { status, task });
          return;
        }
        if (task.type === "STRIP") {
          displayModal("CONFIRM_RESTORE_STRIPPED_ROUTE", { status, task });
          return;
        }
      }

      mutate({ task, payload: { status } });
    },
    [displayModal, mutate],
  );
};
